import { track } from "@amplitude/analytics-react-native";
import toast from "src/swsh-native/toast";
import { useCallback, useMemo, useState } from "react";
import { amp } from "src/api/amplitude";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import { SelectedPhoto } from "src/contexts/GatheringContext";
import { getClientId } from "src/helpers/globalHelpers";
import log from "src/helpers/log";
import useDeletePhotos from "src/hooks/useDeletePhotos";
import { alert } from "src/swsh-native";

const parsePhotosPhrase = (photos: SelectedPhoto[]) => {
	const phrases: string[] = [];
	const numImages = photos.filter((photo) => photo.variant !== PhotoVariant.Video).length;
	const numVideos = photos.filter((photo) => photo.variant === PhotoVariant.Video).length;
	if (numImages > 0) {
		phrases.push(`${numImages} photo${numImages > 1 ? "s" : ""}`);
	}
	if (numVideos > 0) {
		phrases.push(`${numVideos} video${numVideos > 1 ? "s" : ""}`);
	}
	return phrases.join(" and ");
};

const useDeleteSelectedPhotos = (
	photos: SelectedPhoto[],
	{ onComplete }: { onComplete?: () => void } = {},
) => {
	const handleDeletePhotos = useDeletePhotos();
	const [loading, setLoading] = useState(false);
	const hasDelete = useMemo(() => {
		const userId = getClientId();
		return photos.length > 0 && photos.every((photo) => photo.ownerId === userId);
	}, [photos]);

	const handleDelete = useCallback(async () => {
		try {
			const userId = getClientId();
			const ownPhotos = photos.filter((photo) => photo.ownerId === userId);
			// Non-owned photos are protected in the backend anyways, but this is just an extra guard
			if (ownPhotos.length < photos.length) {
				log.error("Non-owned photo somehow leaked into handleDelete");
			}
			track(amp.gathering.deletePhotos.confirm, {
				numPhotos: ownPhotos.length,
			});
			await handleDeletePhotos(ownPhotos.map((photo) => photo.photoId));
			toast({
				preset: "done",
				title: "Success",
				message: `${parsePhotosPhrase(ownPhotos)} deleted successfully`,
			});
		} catch (err) {
			log.error({ err });
			toast({
				preset: "error",
				title: "Error",
				message: "Failed to delete content",
			});
		} finally {
			onComplete?.();
			setLoading(false);
		}
	}, [photos, handleDeletePhotos, onComplete]);

	const promptDeletePhotos = useCallback(async () => {
		setLoading(true);
		track(amp.gathering.deletePhotos.initiate, {
			numPhotos: photos.length,
		});
		alert({
			title: "Delete content",
			message: `Are you sure you want to delete ${parsePhotosPhrase(photos)}?`,
			buttons: [
				{
					text: "Cancel",
					onPress: () => {
						setLoading(false);
						onComplete?.();
						track(amp.gathering.deletePhotos.cancel, {
							numPhotos: photos.length,
						});
					},
					style: "cancel",
				},
				{
					text: "Delete",
					onPress: async () => {
						await handleDelete();
					},
					style: "destructive",
				},
			],
		});
	}, [handleDelete, onComplete, photos]);

	return {
		hasDelete,
		promptDeletePhotos,
		deleteLoading: loading,
	};
};
export default useDeleteSelectedPhotos;
